import { Component } from '@theme/component';
import { debounce, scrollToCenterHorizontal } from '@theme/utilities';
import { animate } from '@theme/vendor';

class RevealCarousel extends Component {
  #targetIndex = 0;
  #autoplayInterval = null;
  #isAutoplayPaused = false;
  #isAnimating = false;
  #queuedIndex = null;
  #resizeObserver = null;
  #resizeDebounce = null;
  #paginationContainer = null;
  #cachedCells = null;
  #swipeState = {
    tracking: false,
    startTime: 0,
    startX: 0,
    startY: 0,
  };

  connectedCallback() {
    super.connectedCallback();

    // Cache cells once on init
    this.#cachedCells = Array.from(this.querySelectorAll('.reveal-carousel-item'));

    const selectedIndex = Math.max(0, this.#cachedCells.findIndex((cell) => cell.classList.contains('is-selected')));
    this.#targetIndex = selectedIndex;

    const listenerOptions = { signal: this.abortController.signal };

    this.#setupNavigationButtons(listenerOptions);
    this.#setupPaginationButtons(listenerOptions);
    this.#setupSwipeGesture();
    this.#setupAutoplayPauseHandlers(listenerOptions);
    this.#setupDesignModeHandlers(listenerOptions);
    this.#setupResizeObserver();

    this.#syncSelectedState(true);
    this.#setupAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearAutoplay();
    this.#resizeDebounce?.cancel?.();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    this.#cachedCells = null;
    this.#paginationContainer = null;
  }

  get cells() {
    return (this.#cachedCells ?? Array.from(this.querySelectorAll('.reveal-carousel-item')))
      .filter((cell) => !cell.hasAttribute('hidden'));
  }

  get selectedCell() {
    return this.cells[this.#targetIndex];
  }

  get selectedIndex() {
    return this.#targetIndex;
  }

  #setupNavigationButtons(listenerOptions) {
    const prevButton = this.querySelector('.reveal-carousel-btn--prev');
    const nextButton = this.querySelector('.reveal-carousel-btn--next');

    prevButton?.addEventListener('click', () => {
      this.previous();
      this.#pauseAutoplay();
    }, listenerOptions);

    nextButton?.addEventListener('click', () => {
      this.next();
      this.#pauseAutoplay();
    }, listenerOptions);
  }

  #setupPaginationButtons(listenerOptions) {
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.reveal-carousel__pagination-button');
      if (!button) return;

      const container = this.#paginationContainer ?? this.querySelector('.reveal-carousel__pagination');
      const buttons = container?.querySelectorAll('.reveal-carousel__pagination-button');
      if (!buttons) return;

      const index = Array.from(buttons).indexOf(button);
      if (index >= 0) {
        this.setSelectedIndex(index);
        this.#pauseAutoplay();
      }
    }, listenerOptions);
  }

  #setupAutoplayPauseHandlers(listenerOptions) {
    // Hover pausing is opt-out; focus pausing always stays on for keyboard users.
    if (this.dataset.pauseOnHover !== 'false') {
      this.addEventListener('mouseenter', () => this.#pauseAutoplay(), listenerOptions);
      this.addEventListener('mouseleave', () => this.#resumeAutoplay(), listenerOptions);
    }
    this.addEventListener('focusin', () => this.#pauseAutoplay(), listenerOptions);
    this.addEventListener('focusout', () => this.#resumeAutoplay(), listenerOptions);
  }

  #setupDesignModeHandlers(listenerOptions) {
    if (!Shopify.designMode) return;

    this.addEventListener('shopify:block:select', (event) => {
      this.#pauseAutoplay();
      let index = -1;

      const carouselItem = event.target.closest('.reveal-carousel-item');
      if (carouselItem) {
        index = this.cells.indexOf(carouselItem);
      } else {
        const paginationBtn = event.target.closest('.reveal-carousel__pagination-button');
        if (paginationBtn) {
          const buttons = this.querySelectorAll('.reveal-carousel__pagination-button');
          index = Array.from(buttons).indexOf(paginationBtn);
        }
      }

      if (index >= 0) this.setSelectedIndex(index, { instant: event.detail.load });
    }, listenerOptions);

    this.addEventListener('shopify:block:deselect', () => this.#resumeAutoplay(), listenerOptions);
  }

  #setupResizeObserver() {
    this.#paginationContainer = this.querySelector('.reveal-carousel__pagination');
    if (!this.#paginationContainer) return;

    this.#resizeDebounce = debounce(() => {
      if (this.#targetIndex >= 0) this.alignSelectedPagination(this.#targetIndex);
    }, 150);

    this.#resizeObserver = new ResizeObserver(this.#resizeDebounce);
    this.#resizeObserver.observe(this.#paginationContainer);
  }

  #setupSwipeGesture() {
    if (this.cells.length <= 1) return;

    const listenerOptions = { signal: this.abortController.signal };
    const THRESHOLD_DISTANCE = 80;
    const THRESHOLD_TIME = 500;
    const PREVENT_SCROLL_THRESHOLD = 10;

    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.#swipeState.tracking = true;
      this.#swipeState.startTime = Date.now();
      this.#swipeState.startX = e.clientX;
      this.#swipeState.startY = e.clientY;
    }, listenerOptions);

    this.addEventListener('pointermove', (e) => {
      if (!this.#swipeState.tracking) return;
      if (Math.abs(e.clientX - this.#swipeState.startX) > PREVENT_SCROLL_THRESHOLD) {
        e.preventDefault();
      }
    }, { passive: false, ...listenerOptions });

    const onPointerEnd = (e) => {
      if (!this.#swipeState.tracking) return;
      const { startTime, startX, startY } = this.#swipeState;
      this.#swipeState.tracking = false;

      if (Date.now() - startTime > THRESHOLD_TIME) return;

      const deltaX = e.clientX - startX;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(e.clientY - startY);

      if (absDeltaX < THRESHOLD_DISTANCE || absDeltaY >= THRESHOLD_DISTANCE) return;

      if (deltaX > 0) {
        this.previous();
      } else {
        this.next();
      }
      this.#pauseAutoplay();
    };

    this.addEventListener('pointerup', onPointerEnd, listenerOptions);
    this.addEventListener('pointerleave', onPointerEnd, listenerOptions);
    this.addEventListener('pointercancel', onPointerEnd, listenerOptions);
  }

  #setupAutoplay() {
    if (this.cells.length <= 1) return;

    const autoplayValue = this.getAttribute('data-autoplay');
    if (!autoplayValue) return;

    const autoplaySpeed = (parseInt(autoplayValue, 10) || 5) * 1000;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.#resumeAutoplay();
      } else {
        this.#pauseAutoplay();
      }
    }, { threshold: 0.5 });

    observer.observe(this);
    this.abortController.signal.addEventListener('abort', () => observer.disconnect());

    this.#startAutoplay(autoplaySpeed);
  }

  #startAutoplay(speed) {
    this.#clearAutoplay();
    this.#autoplayInterval = setInterval(() => {
      if (!this.#isAutoplayPaused) this.next();
    }, speed);
  }

  #pauseAutoplay() {
    this.#isAutoplayPaused = true;
  }

  #resumeAutoplay() {
    if (this.#autoplayInterval) this.#isAutoplayPaused = false;
  }

  #clearAutoplay() {
    if (this.#autoplayInterval) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
    this.#isAutoplayPaused = false;
  }

  /** Sync is-selected classes + pagination + reveal-initialized in one DOM pass */
  #syncSelectedState(init = false) {
    const cells = this.#cachedCells ?? this.cells;
    cells.forEach((cell, i) => cell.classList.toggle('is-selected', i === this.#targetIndex));

    const buttons = this.querySelectorAll('.reveal-carousel__pagination-button');
    buttons.forEach((btn, i) => btn.setAttribute('aria-current', i === this.#targetIndex));

    if (init) this.classList.add('reveal-initialized');
  }

  previous({ instant = false } = {}) {
    const newIndex = (this.#targetIndex - 1 + this.cells.length) % this.cells.length;
    return this.setSelectedIndex(newIndex, { instant });
  }

  next({ instant = false } = {}) {
    const newIndex = (this.#targetIndex + 1) % this.cells.length;
    return this.setSelectedIndex(newIndex, { instant });
  }

  async setSelectedIndex(index, { instant = false, alignPagination = true } = {}) {
    const validIndex = Math.min(Math.max(index, 0), this.cells.length - 1);

    if (this.#isAnimating) {
      this.#queuedIndex = validIndex;
      return;
    }

    await this.animateCellTransition(validIndex, { instant, alignPagination });
  }

  async animateCellTransition(validIndex, { instant = false, alignPagination = true } = {}) {
    const currentCell = this.selectedCell;
    const newCell = this.cells[validIndex];

    if (!currentCell || !newCell || currentCell === newCell) {
      this.#updateLayout(validIndex, alignPagination);
      return;
    }

    if (!instant) {
      this.#isAnimating = true;
      try {
        // 1. Leave: current cell fades out and moves up
        await animate(
          currentCell,
          { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-10px)'] },
          { duration: 250, easing: 'cubic-bezier(0.55, 0.055, 0.675, 0.19)', fill: 'forwards' }
        ).finished;

        this.#updateLayout(validIndex, alignPagination);
        currentCell.style.opacity = '';
        currentCell.style.transform = '';

        // 2. Enter: new cell fades in from below
        await animate(
          newCell,
          { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0px)'] },
          { duration: 400, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)', fill: 'forwards' }
        ).finished;

        newCell.style.opacity = '';
        newCell.style.transform = '';
      } finally {
        this.#isAnimating = false;

        if (this.#queuedIndex != null && this.#queuedIndex !== this.#targetIndex) {
          const queuedIndex = this.#queuedIndex;
          this.#queuedIndex = null;
          // Run the latest requested transition.
          await this.animateCellTransition(queuedIndex, { instant: false, alignPagination });
        } else {
          this.#queuedIndex = null;
        }
      }
    } else {
      this.#updateLayout(validIndex, alignPagination);
    }

    this.dispatchEvent(new CustomEvent('reveal-carousel:settle', {
      detail: { index: validIndex, cell: newCell }
    }));
  }

  #updateLayout(index, alignPagination = true) {
    const previousIndex = this.#targetIndex;
    this.#targetIndex = index;

    this.#syncSelectedState();

    if (alignPagination) this.alignSelectedPagination(index);

    this.dispatchEvent(new CustomEvent('reveal-carousel:select', {
      detail: { index, cell: this.cells[index] }
    }));

    this.dispatchEvent(new CustomEvent('reveal-carousel:change', {
      detail: { index, cell: this.cells[index], direction: index > previousIndex ? 'next' : 'previous' }
    }));
  }

  alignSelectedPagination(index) {
    if (!this.#paginationContainer) {
      this.#paginationContainer = this.querySelector('.reveal-carousel__pagination');
    }
    if (!this.#paginationContainer) return;

    const targetButton = this.#paginationContainer
      .querySelectorAll('.reveal-carousel__pagination-button')[index];
    if (targetButton) scrollToCenterHorizontal(this.#paginationContainer, targetButton);
  }
}

if (!customElements.get('reveal-carousel')) {
  customElements.define('reveal-carousel', RevealCarousel);
}
